import React, { useContext, useEffect } from "react";
import { AlertContext } from "@actinc/dls/context";
import _ from "lodash/fp";
import { Button, Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useMutation } from "urql";

import {
  Backdrop,
  ControlledFormSelect,
  AlertDialog,
} from "../../../components";

const CHUNKSIZE = 100;

const MoveDialog = ({
  optionsToMove,
  defaultValues = {
    newProgram: "",
  },
  handleRefetch,
  onClose,
  objectType,
  programOptions = [],
}) => {
  const alertContext = useContext(AlertContext);

  const formattedObjectType = _.capitalize(objectType);

  const { control, formState, handleSubmit, reset, watch } = useForm({
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues,
  });
  const { isDirty, isSubmitting, isValid } = formState;
  const { newProgram } = watch();

  const [result, move] = useMutation(
    objectType === "passage" ? movePassageMutation : moveItemMutation,
  );

  const onSubmit = async (e) => {
    const objectIdChunks = _.chunk(CHUNKSIZE)(
      optionsToMove.map((object) => object?.id),
    );

    const movePromises = objectIdChunks.map((ids) => {
      return move({
        associatedItemIDs:
          objectType === "passage"
            ? optionsToMove.flatMap((passage) => passage?.associatedItemIDs)
            : undefined,
        ids,
        newProgram,
      });
    });

    const responses = await Promise.allSettled(movePromises);
    const [successfulObjects, errorObjects] = responses.reduce(
      ([countArr, errorArr], result) => {
        if (result.status === "fulfilled") {
          return [
            [
              ...countArr,
              result.value?.data[
                objectType === "passage"
                  ? "reprogramPassages"
                  : "reprogramItems"
              ],
            ].flat(Infinity),
            [...errorArr],
          ];
        } else {
          return [[...countArr], [...errorArr, result].flat(Infinity)];
        }
      },
      [[], []],
    );
    console.log("onSubmit.successfulObjects", successfulObjects);
    console.log("onSubmit.errorObjects", errorObjects);

    if (errorObjects.length) {
      alertContext.actions.addAlert({
        message: `Move failed for ${errorObjects.length}!`,
        options: { persist: true, variant: "error" },
      });
      onClose();
    } else {
      alertContext.actions.addAlert({
        message: `Successfully Moved ${successfulObjects.length}!`,
        options: { variant: "success" },
      });
    }

    reset({ ...defaultValues }, { keepValues: true });
  };

  useEffect(() => {
    if (
      result?.data &&
      result.data[`reprogram${formattedObjectType}s`] !== null
    ) {
      handleRefetch();
    }
  }, [formattedObjectType, result.data, handleRefetch]);

  const handleCancel = () => {
    onClose(null);
  };

  const moveItemDialogProps = {
    open: true,
    dialogTitle: `Select Program`,
    customFooter: (
      <>
        <Button onClick={handleCancel} variant="text">
          Cancel
        </Button>
        <Button
          aria-label="Move"
          color="primary"
          disabled={!isValid || isSubmitting || result?.fetching || !isDirty}
          form="moveDialogForm"
          type="submit"
        >
          Move
        </Button>
      </>
    ),
  };

  return (
    <AlertDialog {...moveItemDialogProps}>
      <Backdrop loading={result?.fetching} />
      <Grid sx={{ alignItems: "top" }} container>
        <Grid item md={8}>
          <Typography variant="subtitle1">
            {optionsToMove.length > 1 ? (
              <>
                {`These ${optionsToMove.length} ${formattedObjectType}s
             will be moved to a new program`}
                {optionsToMove.map((item, idx) => (
                  <Typography key={idx} sx={{ paddingLeft: 2 }} variant="body2">
                    {item.name}
                  </Typography>
                ))}
              </>
            ) : (
              `${formattedObjectType} ${optionsToMove[0]?.name} will be moved to a new program `
            )}
          </Typography>
        </Grid>
        <Grid item md={3}>
          <form id="moveDialogForm" onSubmit={handleSubmit(onSubmit)}>
            <ControlledFormSelect
              control={control}
              id="newProgram"
              label="Program"
              fullWidth={true}
              name="newProgram"
              options={programOptions}
              rules={{
                required: true,
              }}
            />
          </form>
        </Grid>
      </Grid>
    </AlertDialog>
  );
};

const moveItemMutation = `
  mutation ReprogramItems($ids: [ID!]!, $newProgram: ID!) {
    reprogramItems(ids: $ids, newProgram: $newProgram) {
      id
    }
  }
`;
const movePassageMutation = `
  mutation ReprogramPassages($ids: [ID!]!, $newProgram: ID!, $associatedItemIDs: [ID!]) {
    reprogramPassages(ids: $ids, newProgram: $newProgram, associatedItemIDs: $associatedItemIDs) {
      id
    }
  }
`;

export { MoveDialog };
